import { useSelector, shallowEqual } from 'react-redux';
import JsonLd from './JsonLd';

interface MenuItemBlock {
  '@type': string;
  title?: string;
  eyebrow?: string;
  items?: Array<{
    title?: string;
    description?: string;
    price?: string;
    allergens?: string[];
  }>;
}

const DIET_MAP: Record<string, string> = {
  vegan: 'https://schema.org/VeganDiet',
  vege: 'https://schema.org/VegetarianDiet',
  vegetarien: 'https://schema.org/VegetarianDiet',
  'sans-gluten': 'https://schema.org/GlutenFreeDiet',
  'gluten-free': 'https://schema.org/GlutenFreeDiet',
};

const normalizeTag = (tag: string): string =>
  (tag || '')
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/\s+/g, '-')
    .trim();

const buildSection = (block: MenuItemBlock) => {
  const items = Array.isArray(block.items) ? block.items : [];
  return {
    '@type': 'MenuSection',
    name: block.title || '',
    description: block.eyebrow || undefined,
    hasMenuItem: items
      .filter((it) => it && it.title)
      .map((it) => {
        const tags = Array.isArray(it.allergens) ? it.allergens : [];
        const suitableForDiet = tags
          .map((t) => DIET_MAP[normalizeTag(t)])
          .filter((x, i, arr) => x && arr.indexOf(x) === i);

        const menuItem: Record<string, any> = {
          '@type': 'MenuItem',
          name: it.title,
          description: it.description || undefined,
        };
        if (it.price) {
          menuItem.offers = {
            '@type': 'Offer',
            price: it.price,
            priceCurrency: 'EUR',
          };
        }
        if (suitableForDiet.length > 0) {
          menuItem.suitableForDiet = suitableForDiet;
        }
        return menuItem;
      }),
  };
};

const MenuJsonLd = () => {
  const content = useSelector(
    (state: any) => state.content?.data,
    shallowEqual,
  );

  if (!content || content['@type'] !== 'Menu') return null;

  const blocks = content.blocks || {};
  const order: string[] = content.blocks_layout?.items || [];

  const sections = order
    .map((key: string) => blocks[key])
    .filter(
      (b: any): b is MenuItemBlock => b && b['@type'] === 'menuSectionBlock',
    )
    .map(buildSection);

  if (sections.length === 0) return null;

  return (
    <JsonLd
      data={{
        '@context': 'https://schema.org',
        '@type': 'Menu',
        name: content.title || 'Carte',
        description: content.description || undefined,
        hasMenuSection: sections,
      }}
      id="menu"
    />
  );
};

export default MenuJsonLd;
